
const CostRecommendationsHelp = () => {
  return (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Cost Optimization Recommendations
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Cost Recommendations page provides AI-powered insights and actionable suggestions to optimize
          your AWS spending. Identify underutilized resources, right-sizing opportunities, and potential
          savings through reserved instances and other cost-saving strategies.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Right-Sizing Recommendations
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Optimize instance sizes based on actual usage patterns and performance metrics.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Oversized Instances:</strong> EC2 and RDS instances with low CPU/memory utilization</li>
          <li><strong>Recommended Size:</strong> Suggested instance type for optimal cost-performance</li>
          <li><strong>Estimated Savings:</strong> Monthly cost reduction from right-sizing</li>
          <li><strong>Usage Analysis:</strong> Historical metrics supporting the recommendation</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Idle & Underutilized Resources
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Identify resources that are running but not actively used or significantly underutilized.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Stopped Instances:</strong> EC2 instances incurring EBS storage costs while stopped</li>
          <li><strong>Idle Load Balancers:</strong> ALB/NLB with no active traffic</li>
          <li><strong>Unattached Volumes:</strong> EBS volumes not attached to any instance</li>
          <li><strong>Unused Elastic IPs:</strong> IP addresses allocated but not associated</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Reserved Instance Recommendations
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Reduce costs through Reserved Instance (RI) or Savings Plan purchases.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>RI Opportunities:</strong> Workloads suitable for 1-year or 3-year commitments</li>
          <li><strong>Savings Potential:</strong> Estimated annual savings from RI purchases</li>
          <li><strong>Utilization Patterns:</strong> Historical usage supporting RI recommendations</li>
          <li><strong>Savings Plans:</strong> Flexible commitment options for compute usage</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Storage Optimization
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Optimize storage costs through tiering, lifecycle policies, and deletion of unused data.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>S3 Lifecycle Policies:</strong> Move infrequently accessed data to cheaper storage classes</li>
          <li><strong>EBS Snapshots:</strong> Old snapshots that can be deleted to reduce costs</li>
          <li><strong>Glacier Migration:</strong> Archive data suitable for long-term storage</li>
          <li><strong>Intelligent Tiering:</strong> Automatic cost optimization for S3 objects</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Spot Instance Opportunities
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Identify workloads suitable for Spot instances to achieve significant cost savings.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Spot-Compatible Workloads:</strong> Batch jobs, testing, and fault-tolerant applications</li>
          <li><strong>Savings Potential:</strong> Up to 90% cost reduction compared to on-demand</li>
          <li><strong>Interruption Handling:</strong> Guidance on handling Spot instance interruptions</li>
          <li><strong>Mixed Fleet Strategy:</strong> Combine on-demand and Spot for optimal cost/reliability</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Implementation Tracking
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Track the status and impact of implemented cost optimization recommendations.
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>Recommendation Status:</strong> Mark as implemented, dismissed, or pending</li>
          <li><strong>Actual Savings:</strong> Verify cost reductions after implementing recommendations</li>
          <li><strong>Implementation Notes:</strong> Document actions taken and results</li>
          <li><strong>ROI Tracking:</strong> Measure total savings achieved through optimization</li>
        </ul>
      </div>

    </>
  );
};

export default CostRecommendationsHelp;
